import type { RfpReview } from '@shared/domain/review';
import { Button, Checkbox, FormControlLabel, FormGroup, FormHelperText } from '@mui/material';
import { useState } from 'react';
import { ReviewStep } from '@shared/domain/review';
import { RFP_FORM_FIELDS } from '../../data';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import { useReviewForm } from '../../hooks/useReviewForm';
import { QuestionRenderer } from '../QuestionRenderer';
import '../../styles/review-form.scss';

interface RfpReviewProps {
  submitReviewForm?: (
    formFields: RfpReview,
    reviewStep: ReviewStep,
    isDraft?: boolean,
  ) => void;
  editMode?: boolean;
  defaultValues?: Partial<RfpReview> & { id?: number };
  handleEditSuccess?: () => void;
  seedLegalCheckFlags?: Record<string, import('../../legal-check/types').LegalCheckFlag[]>;
}

function RfpReviewForm(props: RfpReviewProps) {
  const { submitReviewForm, editMode, defaultValues, handleEditSuccess, seedLegalCheckFlags } = props;
  const [termsAgreed, setTermsAgreed] = useState(false);

  const {
    formFields,
    extendFormFields,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
    getLegalCheckBindings,
  } = useReviewForm<RfpReview>({
    reviewStep: ReviewStep.RFP,
    formFields: RFP_FORM_FIELDS,
    editMode,
    defaultValues,
    onSubmit: submitReviewForm,
    onEditSuccess: handleEditSuccess,
    customValidationFields: !editMode ? { termsAgreed } : {},
    seedLegalCheckFlags,
  });

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setTermsAgreed(checked);
    validateField('termsAgreed', checked);
  };

  return (
    <div className="review-page">
      <div data-testid="rfp-review-step-content-anchor" />

      <header className="review-page__header">
        <p className="review-page__eyebrow">Step 7 · RFP</p>
        <h1 className="review-page__title">
          {editMode ? 'Edit the RFP details' : 'Walk us through the RFP.'}
        </h1>
        <p className="review-page__subtitle">
          Generalize anything tied to specific scoring, decision rubrics, or
          named tools — share patterns, not the exact rubric.
        </p>
      </header>

      <section className="review-section">
        <QuestionRenderer
          questions={RFP_FORM_FIELDS}
          formFields={formFields}
          errors={errors}
          handleFormFieldChange={handleFormFieldChange}
          extendFormFields={extendFormFields}
          validateField={validateField}
          setFormFields={setFormFields}
          getLegalCheckBindings={getLegalCheckBindings}
        />
      </section>

      {!editMode && (
        <div className="review-terms" data-error-name="termsAgreed">
          <p>
            By submitting this deal retro, you agree to our
            {' '}
            <a href="https://www.kandir.io/terms">terms and conditions</a>
            .
          </p>
          <FormGroup>
            <FormControlLabel
              control={(
                <Checkbox
                  data-testid="terms-agree-checkbox"
                  checked={termsAgreed}
                  onChange={handleTermsChange}
                />
              )}
              label="I Agree"
            />
            {errors.termsAgreed && (
              <FormHelperText error>{errors.termsAgreed}</FormHelperText>
            )}
          </FormGroup>
        </div>
      )}

      <div className="review-save-bar">
        <Button
          variant="contained"
          className="review-save-bar__button"
          data-testid="review-save-button"
          onClick={editMode ? handleEdit : handleSubmit}
        >
          {editMode
            ? `Save ${getReviewStepName(ReviewStep.RFP)} changes`
            : 'Complete Deal Retro'}
        </Button>
      </div>
    </div>
  );
}

export default RfpReviewForm;
